import React from 'react';
import { createRoot } from 'react-dom/client';

// Import components
import Carousel from '@components/Carousel';
import EventFilter from '@components/EventFilter';
import BusinessFilter from '@components/BusinessFilter';
import Search from '@components/Search';
import TodaysEvents from '@components/TodaysEvents';
import UpcomingEvents from '@components/UpcomingEvents';
import BusinessEvents from '@components/BusinessEvents';
import HeroSection from '@components/HeroSection';
import LoginForm from '@components/LoginForm';
import SignupForm from '@components/SignupForm';
import ProfileEditor from '@components/ProfileEditor';
import DiscountCode from '@components/DiscountCode';
import ForgotPasswordForm from '@components/ForgotPasswordForm';
import ResetPasswordForm from '@components/ResetPasswordForm';
import ShareButtons from '@components/ShareButtons';
import EventCard from '@components/EventCard';
import HouseholdSection from '@components/HouseholdSection';
import InviteAcceptance from '@components/InviteAcceptance';
import PinkDrop from '@components/PinkDrop/PinkDrop';
import PinnedPost from '@components/PinnedPost';
import Stories from '@components/Stories';

import { createAnalytics } from '../../vendor/supabase-react-event-analytics';
import { supabase } from './auth';
import { initEventNotifications } from './lib/notifications';
import { fetchFeed, fetchFeedPage } from './lib/feed';

/**
 * Main entry point for React components
 * This file mounts React components to DOM elements in the static HTML
 */
const analytics = createAnalytics({
  supabase,
  pathname: window.location.pathname,
  edgeFunction: {
    supabaseUrl: window.PINK_WALLET_CONFIG?.supabaseUrl,
    functionName: 'analytics-track'
  }
});

// Mount Carousel component
const mountCarousel = () => {
  const container = document.getElementById('react-carousel');
  if (container) {
    try {
      const slidesData = JSON.parse(container.dataset.slides || '[]');
      const root = createRoot(container);
      root.render(<Carousel slides={slidesData} analytics={analytics} />);
      console.log('✅ Carousel component mounted with', slidesData.length, 'slides');
    } catch (error) {
      console.error('Error mounting carousel:', error);
    }
  }
};

// Mount Related Events using EventCard components
const mountRelatedEvents = () => {
  const container = document.getElementById('react-related-events');
  if (container) {
    try {
      const eventsData = JSON.parse(container.dataset.events || '[]');
      const businessesData = JSON.parse(container.dataset.businesses || '[]');
      const locationsData = JSON.parse(container.dataset.locations || '[]');

      const findBusiness = (id) => businessesData.find((b) => b.id === id);
      const findLocation = (event) => {
        if (event.location) return event.location;
        if (event.location_id) {
          return locationsData.find((l) => l.id === event.location_id) || null;
        }
        return null;
      };

      const root = createRoot(container);
      root.render(
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {eventsData.map((ev) => (
            <EventCard
              key={ev.id}
              event={{
                ...ev,
                eventDate: ev.event_date || ev.eventDate,
              }}
              business={findBusiness(ev.business_id)}
              location={findLocation(ev)}
              analytics={analytics}
            />
          ))}
        </div>
      );
      console.log('✅ Related events mounted with', eventsData.length, 'events');
    } catch (error) {
      console.error('Error mounting related events:', error);
    }
  }
};

// Mount EventFilter component
const mountEventFilter = () => {
  const container = document.getElementById('react-event-filter');
  if (container) {
    try {
      const eventsData = JSON.parse(container.dataset.events || '[]');
      const businessesData = JSON.parse(container.dataset.businesses || '[]');
      const locationsData = JSON.parse(container.dataset.locations || '[]');
      const root = createRoot(container);
      root.render(<EventFilter events={eventsData} businesses={businessesData} locations={locationsData} analytics={analytics} />);
      console.log('✅ EventFilter component mounted with', eventsData.length, 'events');
    } catch (error) {
      console.error('Error mounting event filter:', error);
    }
  }
};

// Mount BusinessFilter component
const mountBusinessFilter = () => {
  const container = document.getElementById('react-business-filter');
  if (container) {
    try {
      const businessesData = JSON.parse(container.dataset.businesses || '[]');
      const locationsData = JSON.parse(container.dataset.locations || '[]');
      const root = createRoot(container);
      root.render(<BusinessFilter businesses={businessesData} locations={locationsData} analytics={analytics} />);
      console.log('✅ BusinessFilter component mounted with', businessesData.length, 'businesses');
    } catch (error) {
      console.error('Error mounting business filter:', error);
    }
  }
};

// Mount Search component
const mountSearch = () => {
  const container = document.getElementById('react-search');
  if (container) {
    try {
      const eventsData = JSON.parse(container.dataset.events || '[]');
      const businessesData = JSON.parse(container.dataset.businesses || '[]');
      const resourcesData = JSON.parse(container.dataset.resources || '[]');
      const locationsData = JSON.parse(container.dataset.locations || '[]');
      const initialQuery = container.dataset.initialQuery || '';
      const root = createRoot(container);
      root.render(
        <Search
          events={eventsData}
          businesses={businessesData}
          resources={resourcesData}
          locations={locationsData}
          analytics={analytics}
          initialQuery={initialQuery}
        />
      );
      console.log('✅ Search component mounted');
    } catch (error) {
      console.error('Error mounting search:', error);
    }
  }
};

// Mount TodaysEvents component
const mountTodaysEvents = () => {
  const container = document.getElementById('react-todays-events');
  if (container) {
    try {
      const eventsData = JSON.parse(container.dataset.events || '[]');
      const businessesData = JSON.parse(container.dataset.businesses || '[]');
      const locationsData = JSON.parse(container.dataset.locations || '[]');
      const root = createRoot(container);
      root.render(<TodaysEvents events={eventsData} businesses={businessesData} locations={locationsData} analytics={analytics} />);
      console.log('✅ TodaysEvents component mounted with', eventsData.length, 'events');
    } catch (error) {
      console.error('Error mounting todays events:', error);
    }
  }
};

// Mount UpcomingEvents component (replaces TodaysEvents on front page)
const mountUpcomingEvents = () => {
  const container = document.getElementById('react-upcoming-events');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<UpcomingEvents events={[]} businesses={[]} locations={[]} analytics={analytics} loading />);
      subscribeFeed((data) => {
        const feed = (data && data.feed) || { events: [], businesses: [], locations: [] };
        root.render(
          <UpcomingEvents
            events={feed.events}
            businesses={feed.businesses}
            locations={feed.locations}
            analytics={analytics}
            hasMore={Boolean(feed.hasMore)}
            loadMorePage={fetchFeedPage}
            error={!data}
          />
        );
      });
      console.log('✅ UpcomingEvents component mounted (dynamic feed)');
    } catch (error) {
      console.error('Error mounting upcoming events:', error);
    }
  }
};

// Mount BusinessEvents component (home-feed cards on business detail pages)
const mountBusinessEvents = () => {
  const container = document.getElementById('react-business-events');
  if (container) {
    try {
      const eventsData = JSON.parse(container.dataset.events || '[]');
      const businessesData = JSON.parse(container.dataset.businesses || '[]');
      const locationsData = JSON.parse(container.dataset.locations || '[]');
      const root = createRoot(container);
      root.render(
        <BusinessEvents
          events={eventsData}
          businesses={businessesData}
          locations={locationsData}
          analytics={analytics}
          heading={container.dataset.heading || 'Upcoming Events'}
          sectionClass={container.dataset.sectionClass || ''}
          headingClass={container.dataset.headingClass || ''}
        />
      );
      console.log('✅ BusinessEvents component mounted with', eventsData.length, 'events');
    } catch (error) {
      console.error('Error mounting business events:', error);
    }
  }
};

// Mount HeroSection component
const mountHeroSection = () => {
  const container = document.getElementById('react-hero');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<HeroSection analytics={analytics} />);
      console.log('✅ HeroSection component mounted');
    } catch (error) {
      console.error('Error mounting hero section:', error);
    }
  }
};

// Mount HeroCarousel component (removed in feed redesign — no longer mounted)

// Mount PinnedFeedPost component (homepage pinned Pink Wallet post)
const mountPinnedPost = () => {
  const container = document.getElementById('react-pinned-post');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<PinnedPost />);
      console.log('✅ PinnedPost component mounted');
    } catch (error) {
      console.error('Error mounting PinnedPost:', error);
    }
  }
};

// Mount Stories component (homepage story rings)
const mountStories = () => {
  const container = document.getElementById('react-stories');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<Stories stories={[]} analytics={analytics} loading />);
      subscribeFeed((data) => {
        root.render(
          <Stories
            stories={(data && data.stories) || []}
            analytics={analytics}
          />
        );
      });
      console.log('✅ Stories component mounted (dynamic feed)');
    } catch (error) {
      console.error('Error mounting Stories:', error);
    }
  }
};

// Load the front-page feed once and share the result with every island;
// retries once after 5s so the page self-heals if the edge function is
// briefly unavailable (e.g. right after deploy).
const subscribeFeed = (apply) => {
  fetchFeed().then((data) => {
    if (data) {
      apply(data);
      return;
    }
    setTimeout(() => {
      fetchFeed({ force: true }).then((retry) => apply(retry));
    }, 5000);
  });
};

// Mount LoginForm component
const mountLoginForm = () => {
  const container = document.getElementById('react-login-form');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<LoginForm />);
      console.log('✅ LoginForm component mounted');
    } catch (error) {
      console.error('Error mounting login form:', error);
    }
  }
};

// Mount SignupForm component
const mountSignupForm = () => {
  const container = document.getElementById('react-signup-form');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<SignupForm />);
      console.log('✅ SignupForm component mounted');
    } catch (error) {
      console.error('Error mounting signup form:', error);
    }
  }
};

// Mount ProfileEditor component
let profileEditorRoot = null;
const mountProfileEditor = () => {
  const container = document.getElementById('react-profile-editor');
  if (container) {
    try {
      const initialName = container.dataset.name || '';
      const initialPronouns = container.dataset.pronouns || '';
      const initialEmail = container.dataset.email || '';

      // Create root only once, then reuse it
      if (!profileEditorRoot) {
        profileEditorRoot = createRoot(container);
      }

      profileEditorRoot.render(
        <ProfileEditor
          initialName={initialName}
          initialPronouns={initialPronouns}
          initialEmail={initialEmail}
        />
      );
      console.log('✅ ProfileEditor component mounted');
    } catch (error) {
      console.error('Error mounting profile editor:', error);
    }
  }
};

// Mount DiscountCode component
const mountDiscountCode = () => {
  const container = document.getElementById('react-discount-code');
  if (container) {
    try {
      const code = container.dataset.code || '';
      const title = container.dataset.title || '';
      const description = container.dataset.description || '';
      const variant = container.dataset.variant || 'default';
      const root = createRoot(container);
      root.render(
        <DiscountCode
          code={code}
          title={title}
          description={description}
          analytics={analytics}
          variant={variant}
        />
      );
      console.log('✅ DiscountCode component mounted');
    } catch (error) {
      console.error('Error mounting discount code:', error);
    }
  }
};

// Mount ForgotPasswordForm component
const mountForgotPasswordForm = () => {
  const container = document.getElementById('react-forgot-password-form');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<ForgotPasswordForm />);
      console.log('✅ ForgotPasswordForm component mounted');
    } catch (error) {
      console.error('Error mounting forgot password form:', error);
    }
  }
};

// Mount ResetPasswordForm component
const mountResetPasswordForm = () => {
  const container = document.getElementById('react-reset-password-form');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<ResetPasswordForm />);
      console.log('✅ ResetPasswordForm component mounted');
    } catch (error) {
      console.error('Error mounting reset password form:', error);
    }
  }
};

// Mount ShareButtons component
const mountShareButtons = () => {
  const container = document.getElementById('react-share-buttons');
  if (container) {
    try {
      const url = container.dataset.url || '';
      const title = container.dataset.title || '';
      const description = container.dataset.description || '';
      const root = createRoot(container);
      root.render(<ShareButtons url={url} title={title} description={description} analytics={analytics} />);
      console.log('✅ ShareButtons component mounted');
    } catch (error) {
      console.error('Error mounting share buttons:', error);
    }
  }
};

// Mount HouseholdSection component
const mountHouseholdSection = () => {
  const container = document.getElementById('react-household-section');
  if (container) {
    try {
      // Create root only once if possible to avoid re-mounting issues if called multiple times,
      // but here we expect it to be called once or we should handle unmount.
      // Since it's called via window function, let's keep it simple.
      const root = createRoot(container);

      // We expose a function to update/render with data
      window.mountHouseholdReact = (profile, membership, business) => {
        root.render(
          <HouseholdSection
            profile={profile}
            membership={membership}
            householdTitle={business?.title || 'Household'}
          />
        );
      };

      console.log('✅ HouseholdSection mounter registered');
    } catch (error) {
      console.error('Error in household section setup:', error);
    }
  }
};

// Mount InviteAcceptance component
const mountInviteAcceptance = () => {
  const container = document.getElementById('react-invite-acceptance');
  if (container) {
    try {
      // Get code from hash or query
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const inviteCode = hashParams.get('invite');

      if (inviteCode) {
        const root = createRoot(container);
        root.render(<InviteAcceptance inviteCode={inviteCode} />);
        console.log('✅ InviteAcceptance mounted');
      } else {
        container.innerHTML = '<div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">Invalid invite link. Missing code.</div>';
      }
    } catch (error) {
      console.error('Error mounting invite acceptance:', error);
    }
  }
};

// Mount PinkDrop component
const mountPinkDrop = () => {
  const container = document.getElementById('react-pink-drop-game');
  if (container) {
    try {
      const root = createRoot(container);
      root.render(<PinkDrop />);
      console.log('✅ PinkDrop component mounted');
    } catch (error) {
      console.error('Error mounting PinkDrop:', error);
    }
  }
};

// Initialize all React components when DOM is ready
const initReact = () => {
  mountCarousel();
  mountEventFilter();
  mountBusinessFilter();
  mountSearch();
  mountTodaysEvents();
  mountUpcomingEvents();
  mountBusinessEvents();
  mountHeroSection();
  mountPinnedPost();
  mountStories();
  mountLoginForm();
  mountSignupForm();
  mountProfileEditor();
  mountDiscountCode();
  mountForgotPasswordForm();
  mountResetPasswordForm();
  mountShareButtons();
  mountRelatedEvents();
  mountHouseholdSection();
  mountInviteAcceptance();
  mountPinkDrop();
  initEventNotifications();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReact);
} else {
  initReact();
}

// Listen for custom event to mount ProfileEditor after data is loaded
window.addEventListener('mountProfileEditor', () => {
  mountProfileEditor();
});

// Export mount functions for manual mounting if needed
export {
  mountCarousel,
  mountEventFilter,
  mountBusinessFilter,
  mountSearch,
  mountTodaysEvents,
  mountUpcomingEvents,
  mountBusinessEvents,
  mountHeroSection,
  mountPinnedPost,
  mountStories,
  mountHouseholdSection,
  mountInviteAcceptance,
  mountPinkDrop
};
